'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
  getLastSyncTime,
  canManualRefresh,
  currentManualCooldownMs,
  getLatestScoredMatch,
  refetchForSync,
} from '../supabaseData';
import { getSavedTournament } from '../apiHelper';
import { trackEvent } from '../analytics';
import './sync-indicator.css';

const TICK_MS = 30000;
const TOAST_MS = 6000;

function formatRelative(ts) {
  if (!ts) return '…';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function formatAbsolute(ts) {
  if (!ts) return 'Not yet synced';
  return `Last fetched at ${new Date(ts).toLocaleString()}`;
}

function buildLatestMatchMessage(match) {
  if (!match) return 'Synced. No match scores available yet.';
  return `Latest scores available for Match #${match.matchnumber} ${match.matchname} — ${match.day}, ${match.date} at ${match.time}`;
}

const SyncIndicator = () => {
  const [lastSync, setLastSync] = useState(null);
  const [canRefresh, setCanRefresh] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message, variant = 'info') => {
    setToast({ message, variant });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const refresh = useCallback(() => {
    setLastSync(getLastSyncTime());
    setCanRefresh(canManualRefresh());
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, TICK_MS);
    const onCacheEvent = () => setTimeout(refresh, 100);
    window.addEventListener('cacheCleared', onCacheEvent);
    window.addEventListener('cacheWritten', onCacheEvent);
    return () => {
      clearInterval(interval);
      window.removeEventListener('cacheCleared', onCacheEvent);
      window.removeEventListener('cacheWritten', onCacheEvent);
    };
  }, [refresh]);

  const handleClick = useCallback(async () => {
    if (syncing) return;
    if (!canRefresh) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      showToast("You're offline. Connect and try again — your cached data is unchanged.", 'error');
      return;
    }
    setSyncing(true);
    trackEvent('manual_refresh');
    try {
      const tournamentId = getSavedTournament();
      await refetchForSync(tournamentId);
      window.dispatchEvent(new CustomEvent('cacheCleared'));
      showToast(buildLatestMatchMessage(getLatestScoredMatch()), 'info');
    } catch (e) {
      console.error('Manual refresh failed:', e);
      showToast('Sync failed. Your cached data is unchanged.', 'error');
    } finally {
      setSyncing(false);
      refresh();
    }
  }, [syncing, canRefresh, refresh, showToast]);

  const msUntilAvailable = lastSync
    ? Math.max(0, lastSync + currentManualCooldownMs() - Date.now())
    : 0;
  const waitCopy = msUntilAvailable < 3600000
    ? (() => {
        const mins = Math.max(1, Math.ceil(msUntilAvailable / 60000));
        return `~${mins} min${mins === 1 ? '' : 's'}`;
      })()
    : (() => {
        const hrs = Math.max(1, Math.ceil(msUntilAvailable / 3600000));
        return `~${hrs} hr${hrs === 1 ? '' : 's'}`;
      })();

  const absoluteTime = formatAbsolute(lastSync);
  const btnTooltip = syncing
    ? 'Syncing…'
    : !lastSync
      ? 'Loading latest scores…'
      : canRefresh
        ? `Pull latest scores from the server\n${absoluteTime}`
        : `You already have the latest scores. Next refresh available in ${waitCopy}.\n${absoluteTime}`;

  return (
    <div className="sync-indicator">
      <span className={`sync-indicator-chip has-tooltip${!canRefresh || syncing ? ' disabled' : ''}`} data-tooltip={toast ? undefined : btnTooltip} onClick={handleClick} role="button" tabIndex={0} aria-label={btnTooltip}>
        <span className={`sync-indicator-icon${syncing ? ' spinning' : ''}`}>
          <FontAwesomeIcon icon={faRotate} />
        </span>
        <span className="sync-indicator-label">
          {syncing ? 'Syncing…' : `Synced ${formatRelative(lastSync)}`}
        </span>
      </span>
      {toast && (
        <div className={`sync-indicator-toast sync-indicator-toast--${toast.variant}`} role="status">
          <span className="sync-indicator-toast-message">{toast.message}</span>
          <button
            type="button"
            className="sync-indicator-toast-close"
            onClick={dismissToast}
            aria-label="Dismiss"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SyncIndicator;
